// core/stores/prefsStore.ts — Zustand store for personalisation preferences.
//
// Holds the full UserPreferences object (themeMode + accent + fontFamily +
// textSize + radius + likesPublic + reduceMotion + backdrop + panelSolidity) plus
// the mutations needed by prefsService and hooks.
//
// ── Per-account persistence ────────────────────────────────────────────────
// Strategy: prefsService-mediated read/write (simpler than dynamic persist key).
//
// The store is NOT persisted by Zustand's persist middleware.  Instead,
// prefsService reads & writes localStorage directly, keyed by the active
// account handle at call time:
//   signed-in  → 'truxsocial:prefs:@handle'
//   signed-out → 'truxsocial:prefs:guest'
//
// On app boot, AppThemeProvider (or a bootstrap call from main.tsx) calls
// prefsService.getPreferences() which rehydrates the store for the current
// account.  On sign-in / sign-out, authService calls
// prefsService.getPreferences() again to swap in the new account's prefs.
//
// This avoids Zustand re-creating the store with a new key (which would
// cause a React subtree tear-down) while still keeping prefs per-account.
//
// ── A1 / A2 backward-compatibility ────────────────────────────────────────
// A1 BootScreen + A2 ComponentsGallery use:
//   usePrefsStore(s => s.themeMode)   → mapped from prefs.themeMode
//   usePrefsStore(s => s.toggleTheme) → thin wrapper over setPref
//   usePrefsStore()                   → destructure {themeMode, toggleTheme}
// All three continue to work via the shim selectors below.

import { create } from 'zustand';
import { DEFAULT_ACCOUNT_MENU } from '@/core/models/accountMenu';
import type { UserPreferences } from '@/core/models/preferences';
import {
  RADIUS_PX_RANGE,
  CARD_COLOR_DEFAULT,
  CARD_ELEVATION_DEFAULT,
} from '@/core/models/preferences';
import { useSessionStore } from '@/core/stores/sessionStore';

// ── Internal write-through helper ──────────────────────────────────────────
// Mirrors prefsService's getStorageKey / writeToStorage so that A1/A2 shim
// mutations (toggleTheme / setTheme) also persist per-account without
// importing prefsService (which would create a cycle via Zustand's create).

const PREFS_KEY_PREFIX = 'truxsocial:prefs';

function getPrefsStorageKey(): string {
  const accounts = useSessionStore.getState().accounts;
  const active = accounts.find((a) => a.useThisAsActive);
  return active ? `${PREFS_KEY_PREFIX}:${active.handle}` : `${PREFS_KEY_PREFIX}:guest`;
}

function persistPrefs(prefs: UserPreferences): void {
  try {
    localStorage.setItem(getPrefsStorageKey(), JSON.stringify(prefs));
  } catch {
    // localStorage quota or SSR — ignore
  }
}

export type ThemeMode = 'light' | 'dark';

export const DEFAULT_PREFS: UserPreferences = {
  accountMenuShortcuts: [...DEFAULT_ACCOUNT_MENU],
  themeMode: 'dark',
  accent: 'default',
  accentCustom: '#BB88CB',
  finish: 'flat',
  fontFamily: 'default',
  textSize: 'md',
  radiusPx: RADIUS_PX_RANGE.default,
  radiusMinPx: RADIUS_PX_RANGE.defaultMin,
  radiusMaxPx: RADIUS_PX_RANGE.defaultMax,
  // Card appearance — every material scalar at 0 (and elevation at its neutral 0.5)
  // reproduces the shipped card look byte-for-byte.
  cardColor: CARD_COLOR_DEFAULT,
  cardColorStrength: 0,
  cardSheen: 0,
  cardGrain: 0,
  cardEdge: 0,
  cardElevation: CARD_ELEVATION_DEFAULT,
  overlayGradient: 'aurora',
  overlayGradientStrength: 0.65,
  postGradientStrength: 0.3,
  panelGradientStrength: 0.45,
  likesPublic: false,
  reduceMotion: false,
  bannerAnimations: false,
  underlineLinks: false,
  reduceTransparency: false,
  timestampStyle: 'relative',
  clockFormat: '12h',
  rememberFeedPosition: false,
  defaultFeedTab: 'following',
  feedDensity: 'comfortable',
  feedOrder: 'default',
  backdrop: 'auto',
  bgIntensity: 0.45,
  cardsOverlay: true,
  panelSolidity: 0.8,
  glassBlur: 12,
  glassTint: 0,
  glassSaturation: 1.2,
};

interface PrefsState {
  prefs: UserPreferences;

  // Fine-grained setter: setPref('accent', 'sunset')
  setPref: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;

  // Bulk replace (used by prefsService.getPreferences to rehydrate)
  setPrefs: (prefs: UserPreferences) => void;

  // Reset to defaults
  reset: () => void;

  // ── A1/A2 shim API (BootScreen + ComponentsGallery) ──────────────────────
  // These are derived getters / thin wrappers so existing call sites
  // continue to work without modification.
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

export const usePrefsStore = create<PrefsState>()((set) => ({
  prefs: { ...DEFAULT_PREFS },

  setPref: (key, value) =>
    set((s) => {
      const updated = { ...s.prefs, [key]: value };
      return {
        prefs: updated,
        themeMode: updated.themeMode,
      };
    }),

  setPrefs: (prefs) => set({ prefs, themeMode: prefs.themeMode }),

  reset: () => set({ prefs: { ...DEFAULT_PREFS }, themeMode: DEFAULT_PREFS.themeMode }),

  // ── A1/A2 shim ──────────────────────────────────────────────────────────
  themeMode: DEFAULT_PREFS.themeMode,

  toggleTheme: () =>
    set((s) => {
      const next: ThemeMode = s.prefs.themeMode === 'dark' ? 'light' : 'dark';
      const updated = { ...s.prefs, themeMode: next };
      persistPrefs(updated);
      return { prefs: updated, themeMode: next };
    }),

  setTheme: (mode) =>
    set((s) => {
      const updated = { ...s.prefs, themeMode: mode };
      persistPrefs(updated);
      return { prefs: updated, themeMode: mode };
    }),
}));
